import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { authStorage } from 'utils/authStorage';

import {
  AuthState,
  ClientWorkspace,
  RegistrationMetrics,
  User
} from 'types/auth';

import { Region } from 'constants/enums';
import { INITIAL_USER_CONFIG } from 'constants/uiSettings';

import {
  fetchClientWorkspaceData,
  fetchRegistrationMetrics,
  fetchUserData
} from './authThunks';

const { token, region } = authStorage.getAuthData();

const defaultRegistrationMetrics = {
  applicationsSubmittedThisWeek: 0,
  newCandidatesThisWeek: 0,
  jobseekersMatchingCurrentlyActiveJobs: 0
};

const initialState: AuthState = {
  token,
  region,
  isLoggedIn: authStorage.isAuthenticated(),
  user: INITIAL_USER_CONFIG,
  isLoading: false,
  passwordLength: 0,
  workspace: null,
  workspaceLoading: false,
  registrationMetrics: defaultRegistrationMetrics
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ token: string; region: Region }>
    ) => {
      const { token, region } = action.payload;

      state.token = token;
      state.region = region;
      state.isLoggedIn = true;

      authStorage.setAuthData({ token, region });
    },
    setPasswordLength: (state, action: PayloadAction<number>) => {
      state.passwordLength = action.payload;
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      state.user = {
        ...state.user,
        ...action.payload
      };
    },
    logout: (state) => {
      state.token = null;
      state.region = null;
      state.isLoggedIn = false;
      state.user = INITIAL_USER_CONFIG;
      state.passwordLength = 0;

      authStorage.clearAuthData();
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserData.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(
      fetchUserData.fulfilled,
      (state, action: PayloadAction<User>) => {
        state.user = {
          ...state.user,
          ...action.payload
        };
        state.isLoading = false;
      }
    );
    builder.addCase(fetchUserData.rejected, (state) => {
      state.isLoading = false;
    });
    // Client workspace
    builder.addCase(fetchClientWorkspaceData.pending, (state) => {
      state.workspaceLoading = true;
    });
    builder.addCase(
      fetchClientWorkspaceData.fulfilled,
      (state, action: PayloadAction<ClientWorkspace>) => {
        state.workspaceLoading = false;
        state.workspace = action.payload;
      }
    );
    builder.addCase(fetchClientWorkspaceData.rejected, (state) => {
      state.workspaceLoading = false;
    });

    builder.addCase(
      fetchRegistrationMetrics.fulfilled,
      (state, action: PayloadAction<RegistrationMetrics>) => {
        state.registrationMetrics = action.payload;
      }
    );
    builder.addCase(fetchRegistrationMetrics.rejected, (state) => {
      state.registrationMetrics = defaultRegistrationMetrics;
    });
  }
});

export const { setCredentials, setPasswordLength, updateUser, logout } =
  authSlice.actions;

export default authSlice.reducer;
